import {
    formatCpmCount,
    formatVolumeLabel,
} from '../../../constants/centralProductMaster.constants';
import type { CpmMasterDetailsResponse } from '../../../types/centralProductMaster.types';

type VariantRow = NonNullable<CpmMasterDetailsResponse['affectedVariants']>[number];
type StateRow = NonNullable<VariantRow['stateProducts']>[number];

function volumeKeyOf(v: { volumeMl?: number; volumeUnit?: string }): string {
    return `${v.volumeMl ?? ''}:${v.volumeUnit ?? ''}`;
}

function formatMoney(n: number | null | undefined): string {
    if (n == null || Number.isNaN(Number(n))) return '—';
    return `₹${formatCpmCount(Number(n))}`;
}

function truncName(name: string, max = 28): string {
    if (name.length <= max) return name;
    return `${name.slice(0, max - 1)}…`;
}

function spFieldRows(sp: StateRow | undefined) {
    return [
        { key: 'retail', label: 'Retail price', value: formatMoney(sp?.retailPrice) },
        { key: 'purchase', label: 'Purchase price', value: formatMoney(sp?.purchasePrice) },
        {
            key: 'wholesale',
            label: 'Wholesale price',
            value: sp?.wholesalePrice != null ? formatMoney(sp.wholesalePrice) : '—',
        },
        { key: 'distillery', label: 'Distillery price', value: formatMoney(sp?.distilleryPrice) },
        {
            key: 'excise',
            label: 'Excise category',
            value: sp?.exciseCategoryName || '—',
        },
        {
            key: 'exciseSub',
            label: 'Subcategory',
            value: sp?.exciseSubCategoryName || '—',
        },
        { key: 'itemCode', label: 'Item code', value: sp?.itemCode || '—' },
    ];
}

function findSp(list: StateRow[] | undefined, state: string): StateRow | undefined {
    return (list ?? []).find((sp) => String(sp.state ?? '') === state);
}

function RadioDot({ chosen }: { chosen: boolean }) {
    return (
        <span
            className="inline-flex items-center justify-center shrink-0"
            style={{
                width: 17,
                height: 17,
                borderRadius: 999,
                border: `1.5px solid ${chosen ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                background: chosen ? 'var(--cpm-brand-purple)' : '#fff',
            }}
        >
            {chosen ? (
                <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="3.4"
                >
                    <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ) : null}
        </span>
    );
}

export interface ProductVolumeCollisionsProps {
    sideA: CpmMasterDetailsResponse;
    sideB: CpmMasterDetailsResponse;
    idA: string;
    idB: string;
    survivorId: string | null;
    volumeChoices: Record<string, string>;
    volStateChoices: Record<string, Record<string, string>>;
    openVolStates: Record<string, boolean>;
    onPickVolume: (volumeKey: string, variantId: string) => void;
    onPickVolState: (volumeKey: string, state: string, variantId: string) => void;
    onToggleVolStates: (volumeKey: string) => void;
    /** Nested under a brand same-name pair — skip outer card chrome; hide when no volume overlap. */
    embedded?: boolean;
}

export default function ProductVolumeCollisions({
    sideA,
    sideB,
    idA,
    idB,
    survivorId,
    volumeChoices,
    volStateChoices,
    openVolStates,
    onPickVolume,
    onPickVolState,
    onToggleVolStates,
    embedded = false,
}: ProductVolumeCollisionsProps) {
    const productName = (id: string) => String((id === idA ? sideA : sideB).record?.name ?? id);

    const variantsA = sideA.affectedVariants ?? [];
    const variantsB = sideB.affectedVariants ?? [];

    const allKeys = Array.from(new Set([...variantsA, ...variantsB].map((v) => volumeKeyOf(v))));
    const collidingKeys = allKeys.filter(
        (key) =>
            variantsA.some((v) => volumeKeyOf(v) === key) &&
            variantsB.some((v) => volumeKeyOf(v) === key)
    );
    const carryKeys = allKeys.filter((key) => !collidingKeys.includes(key));

    if (collidingKeys.length === 0 && (embedded || carryKeys.length === 0)) return null;

    const unresolved = collidingKeys.filter((k) => !volumeChoices[k]);
    const nestedUnresolvedCount = collidingKeys.reduce((acc, key) => {
        const a = variantsA.find((v) => volumeKeyOf(v) === key);
        const b = variantsB.find((v) => volumeKeyOf(v) === key);
        if (!a || !b) return acc;
        const states = Array.from(
            new Set(
                [...(a.stateProducts ?? []), ...(b.stateProducts ?? [])]
                    .map((sp) => String(sp.state ?? ''))
                    .filter(Boolean)
            )
        );
        const overlap = states.filter(
            (s) => findSp(a.stateProducts, s) && findSp(b.stateProducts, s)
        );
        const chosen = volStateChoices[key] ?? {};
        return acc + overlap.filter((s) => !chosen[s]).length;
    }, 0);

    const unresolvedVolumes = unresolved.length;
    const subhead =
        collidingKeys.length === 0
            ? 'No same-volume collisions — every variant re-parents as it is.'
            : unresolvedVolumes > 0
              ? unresolvedVolumes === 1
                  ? `1 of ${formatCpmCount(collidingKeys.length)} colliding volume${collidingKeys.length === 1 ? '' : 's'} still needs a decision — only one variant can survive per volume under the merged product.`
                  : `${formatCpmCount(unresolvedVolumes)} of ${formatCpmCount(collidingKeys.length)} colliding volumes still need a decision — only one variant can survive per volume under the merged product.`
              : nestedUnresolvedCount > 0
                ? `${formatCpmCount(nestedUnresolvedCount)} nested state pricing decision${nestedUnresolvedCount === 1 ? '' : 's'} still open under resolved volumes.`
                : 'All colliding volumes resolved. Variants at every other volume are re-parented untouched.';

    const labelForKey = (key: string) => {
        const v =
            variantsA.find((x) => volumeKeyOf(x) === key) ??
            variantsB.find((x) => volumeKeyOf(x) === key);
        return v ? formatVolumeLabel(v.volumeMl, v.volumeUnit) : key;
    };

    return (
        <div
            className={
                embedded
                    ? 'flex flex-col'
                    : 'bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col'
            }
            style={
                embedded
                    ? { gap: 12 }
                    : {
                          borderRadius: 16,
                          padding: '20px 22px',
                          gap: 16,
                          boxShadow:
                              '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                      }
            }
        >
            {embedded ? null : (
            <div className="flex items-baseline flex-wrap" style={{ gap: 12 }}>
                <span
                    className="uppercase text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                >
                    Same-volume variant collisions
                </span>
                <span
                    className="text-pretty"
                    style={{
                        fontSize: 13,
                        fontWeight: unresolvedVolumes > 0 || nestedUnresolvedCount > 0 ? 700 : 600,
                        color:
                            unresolvedVolumes > 0 || nestedUnresolvedCount > 0
                                ? 'var(--cpm-warning-text)'
                                : 'var(--cpm-text-secondary)',
                    }}
                >
                    {subhead}
                </span>
            </div>
            )}

            {carryKeys.length > 0 ? (
                <div
                    className="flex flex-col border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)]"
                    style={{ gap: 8, padding: '13px 15px', borderRadius: 12 }}
                >
                    <span
                        className="text-[var(--cpm-text-primary)]"
                        style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '-0.01em' }}
                    >
                        {carryKeys.length === 1
                            ? '1 volume exists on only one product — that variant is re-parented as it is, nothing to decide'
                            : `${formatCpmCount(carryKeys.length)} volumes exist on only one product — those variants are re-parented as they are, nothing to decide`}
                    </span>
                    <div className="flex flex-wrap" style={{ gap: 6 }}>
                        {carryKeys.map((key) => {
                            const onA = variantsA.some((v) => volumeKeyOf(v) === key);
                            const from = onA ? productName(idA) : productName(idB);
                            return (
                                <span
                                    key={key}
                                    className="inline-flex items-center bg-white border border-[var(--cpm-border-default)] text-[var(--cpm-text-primary)]"
                                    style={{
                                        gap: 6,
                                        padding: '4px 10px',
                                        borderRadius: 999,
                                        fontSize: 11.5,
                                        fontWeight: 800,
                                    }}
                                >
                                    {labelForKey(key)}
                                    <span
                                        className="text-[var(--cpm-text-tertiary)] whitespace-nowrap"
                                        style={{ fontWeight: 700 }}
                                    >
                                        {survivorId
                                            ? onA === (survivorId === idA)
                                                ? 'from the survivor'
                                                : 'from the retiring record'
                                            : `on ${truncName(from)}`}
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            <div className="flex flex-col" style={{ gap: 14 }}>
                {collidingKeys.map((key) => {
                    const a = variantsA.find((v) => volumeKeyOf(v) === key)!;
                    const b = variantsB.find((v) => volumeKeyOf(v) === key)!;
                    const chosenId = volumeChoices[key] || null;
                    const done = !!chosenId;
                    const options: Array<{
                        variant: VariantRow;
                        productId: string;
                        productLabel: string;
                    }> = [
                        { variant: a, productId: idA, productLabel: productName(idA) },
                        { variant: b, productId: idB, productLabel: productName(idB) },
                    ];

                    const states = Array.from(
                        new Set(
                            [...(a.stateProducts ?? []), ...(b.stateProducts ?? [])]
                                .map((sp) => String(sp.state ?? ''))
                                .filter(Boolean)
                        )
                    );
                    const overlap = states.filter(
                        (s) => findSp(a.stateProducts, s) && findSp(b.stateProducts, s)
                    );
                    const carryStates = states.filter((s) => !overlap.includes(s));
                    const volMap = volStateChoices[key] ?? {};
                    const nestedUnres = overlap.filter((s) => !volMap[s]);
                    const statesOpen = openVolStates[key] !== false;
                    const hasStateNested = overlap.length > 0;

                    return (
                        <div
                            key={key}
                            className="flex flex-col"
                            style={{
                                gap: 10,
                                padding: '14px 15px',
                                borderRadius: 12,
                                border: `1px solid ${done ? 'var(--cpm-border-default)' : 'var(--cpm-warning-border)'}`,
                                background: done
                                    ? 'var(--cpm-surface-subtle)'
                                    : 'var(--cpm-warning-bg)',
                            }}
                        >
                            <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                                <span
                                    className="text-[var(--cpm-brand-navy)]"
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 800,
                                        letterSpacing: '-0.015em',
                                    }}
                                >
                                    {labelForKey(key)}
                                </span>
                                <span
                                    className="inline-flex items-center rounded-full uppercase"
                                    style={{
                                        padding: '3px 9px',
                                        fontSize: 9.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                        background: done
                                            ? 'var(--cpm-success-bg)'
                                            : 'var(--cpm-warning-bg)',
                                        border: `1px solid ${done ? 'var(--cpm-success-border)' : 'var(--cpm-warning-border)'}`,
                                        color: done
                                            ? 'var(--cpm-success-text-light)'
                                            : 'var(--cpm-warning-text)',
                                    }}
                                >
                                    {done ? 'Resolved' : 'Decision needed'}
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                    style={{ fontSize: 12, fontWeight: 600 }}
                                >
                                    {done
                                        ? 'The variant you picked survives at this volume; the other is dropped.'
                                        : 'Both products have a variant at this volume — pick the one that survives under the merged product.'}
                                </span>
                            </div>

                            <div
                                className="grid"
                                style={{
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: 10,
                                    alignItems: 'start',
                                }}
                            >
                                {options.map((opt) => {
                                    const isChosen = chosenId === opt.variant._id;
                                    const role =
                                        survivorId == null
                                            ? 'none'
                                            : survivorId === opt.productId
                                              ? 'survivor'
                                              : 'retiring';
                                    const other = opt.variant._id === a._id ? b : a;
                                    const fields = [
                                        {
                                            key: 'name',
                                            label: 'Variant name',
                                            value: opt.variant.name,
                                            otherVal: other.name,
                                        },
                                        {
                                            key: 'volume',
                                            label: 'Volume',
                                            value: formatVolumeLabel(
                                                opt.variant.volumeMl,
                                                opt.variant.volumeUnit
                                            ),
                                            otherVal: formatVolumeLabel(
                                                other.volumeMl,
                                                other.volumeUnit
                                            ),
                                        },
                                        {
                                            key: 'status',
                                            label: 'Status',
                                            value: opt.variant.status,
                                            otherVal: other.status,
                                        },
                                    ];
                                    return (
                                        <button
                                            key={opt.variant._id}
                                            type="button"
                                            onClick={() => onPickVolume(key, opt.variant._id)}
                                            className="flex flex-col text-left bg-white"
                                            style={{
                                                gap: 9,
                                                padding: '13px 14px',
                                                borderRadius: 12,
                                                border: `1.5px solid ${isChosen ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-default)'}`,
                                                background: isChosen
                                                    ? 'rgba(110,100,240,.07)'
                                                    : '#fff',
                                                boxShadow: isChosen
                                                    ? '0 0 0 3px rgba(110,100,240,.20)'
                                                    : undefined,
                                            }}
                                        >
                                            <div className="flex items-center" style={{ gap: 8 }}>
                                                <RadioDot chosen={isChosen} />
                                                <span
                                                    className="min-w-0 truncate text-[var(--cpm-text-primary)]"
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                    }}
                                                >
                                                    {opt.productLabel}
                                                </span>
                                                <span
                                                    className="ml-auto inline-flex items-center rounded-full uppercase shrink-0"
                                                    style={{
                                                        padding: '2px 8px',
                                                        fontSize: 9.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.05em',
                                                        background:
                                                            role === 'survivor'
                                                                ? 'var(--cpm-purple-tint-13)'
                                                                : role === 'retiring'
                                                                  ? 'var(--cpm-shortfall-bg)'
                                                                  : 'var(--cpm-surface-skeleton)',
                                                        border:
                                                            role === 'retiring'
                                                                ? '1px solid var(--cpm-shortfall-border)'
                                                                : undefined,
                                                        color:
                                                            role === 'survivor'
                                                                ? 'var(--cpm-brand-purple)'
                                                                : role === 'retiring'
                                                                  ? 'var(--cpm-shortfall-text)'
                                                                  : 'var(--cpm-text-secondary)',
                                                    }}
                                                >
                                                    {role === 'survivor'
                                                        ? 'Survivor'
                                                        : role === 'retiring'
                                                          ? 'Retiring record'
                                                          : 'On file'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                {fields.map((f, i) => {
                                                    const differs = f.value !== f.otherVal;
                                                    return (
                                                        <div
                                                            key={f.key}
                                                            className="flex items-center justify-between"
                                                            style={{
                                                                gap: 10,
                                                                padding: '6px 0',
                                                                borderBottom:
                                                                    i < fields.length - 1
                                                                        ? '1px solid var(--cpm-border-default)'
                                                                        : undefined,
                                                            }}
                                                        >
                                                            <span
                                                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                                                style={{
                                                                    fontSize: 10.5,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '0.05em',
                                                                }}
                                                            >
                                                                {f.label}
                                                            </span>
                                                            <span
                                                                className="tabular text-right min-w-0 break-words"
                                                                style={{
                                                                    fontSize: 12.5,
                                                                    fontWeight: 800,
                                                                    color: differs
                                                                        ? 'var(--cpm-brand-navy)'
                                                                        : 'var(--cpm-text-secondary)',
                                                                    background: differs
                                                                        ? 'rgba(110,100,240,.09)'
                                                                        : undefined,
                                                                    borderRadius: differs
                                                                        ? 6
                                                                        : undefined,
                                                                    padding: differs
                                                                        ? '1px 6px'
                                                                        : undefined,
                                                                }}
                                                            >
                                                                {f.value}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {hasStateNested ? (
                                <div
                                    className="flex flex-col bg-white border border-[var(--cpm-border-default)] overflow-hidden"
                                    style={{ borderRadius: 12 }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => onToggleVolStates(key)}
                                        className="flex items-center w-full border-0 cursor-pointer text-left"
                                        style={{
                                            gap: 9,
                                            padding: '11px 13px',
                                            background:
                                                nestedUnres.length === 0
                                                    ? 'var(--cpm-surface-subtle)'
                                                    : 'rgba(212,145,6,.06)',
                                        }}
                                    >
                                        <span
                                            className="inline-flex items-center justify-center shrink-0 bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                            style={{ width: 24, height: 24, borderRadius: 7 }}
                                        >
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                            >
                                                <path d="M12 2 2 7l10 5 10-5-10-5Z" />
                                                <path d="m2 17 10 5 10-5" />
                                                <path d="m2 12 10 5 10-5" />
                                            </svg>
                                        </span>
                                        <span
                                            className="uppercase text-[var(--cpm-text-secondary)]"
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 800,
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            State pricing overlap
                                        </span>
                                        <span
                                            className="inline-flex items-center rounded-full uppercase"
                                            style={{
                                                padding: '3px 9px',
                                                fontSize: 9.5,
                                                fontWeight: 800,
                                                letterSpacing: '0.06em',
                                                background:
                                                    nestedUnres.length === 0
                                                        ? 'var(--cpm-success-bg)'
                                                        : 'var(--cpm-warning-bg)',
                                                border: `1px solid ${nestedUnres.length === 0 ? 'var(--cpm-success-border)' : 'var(--cpm-warning-border)'}`,
                                                color:
                                                    nestedUnres.length === 0
                                                        ? 'var(--cpm-success-text-light)'
                                                        : 'var(--cpm-warning-text)',
                                            }}
                                        >
                                            {nestedUnres.length === 0
                                                ? 'States resolved'
                                                : `${formatCpmCount(nestedUnres.length)} of ${formatCpmCount(overlap.length)} states`}
                                        </span>
                                        <span
                                            className="ml-auto inline-flex text-[var(--cpm-text-tertiary)]"
                                            style={{
                                                transform: statesOpen
                                                    ? 'rotate(180deg)'
                                                    : undefined,
                                                transition: 'transform 150ms ease',
                                            }}
                                        >
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.4"
                                            >
                                                <path
                                                    d="m6 9 6 6 6-6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </button>

                                    {statesOpen ? (
                                        <div
                                            className="flex flex-col border-t border-[var(--cpm-border-default)]"
                                            style={{ gap: 12, padding: '13px 14px' }}
                                        >
                                            {carryStates.length > 0 ? (
                                                <div
                                                    className="flex flex-col border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)]"
                                                    style={{
                                                        gap: 8,
                                                        padding: '11px 13px',
                                                        borderRadius: 11,
                                                    }}
                                                >
                                                    <span
                                                        className="text-[var(--cpm-text-primary)]"
                                                        style={{
                                                            fontSize: 11.5,
                                                            fontWeight: 800,
                                                            letterSpacing: '-0.01em',
                                                        }}
                                                    >
                                                        {formatCpmCount(carryStates.length)} state
                                                        {carryStates.length === 1 ? '' : 's'}{' '}
                                                        {carryStates.length === 1 ? 'is' : 'are'}{' '}
                                                        priced on only one of these variants —{' '}
                                                        {carryStates.length === 1 ? 'it' : 'they'}{' '}
                                                        carry over unchanged
                                                    </span>
                                                    <div
                                                        className="flex flex-wrap"
                                                        style={{ gap: 6 }}
                                                    >
                                                        {carryStates.map((state) => {
                                                            const onA = !!findSp(
                                                                a.stateProducts,
                                                                state
                                                            );
                                                            const vari = onA ? a : b;
                                                            return (
                                                                <span
                                                                    key={state}
                                                                    className="inline-flex items-center bg-white border border-[var(--cpm-border-default)]"
                                                                    style={{
                                                                        gap: 6,
                                                                        padding: '4px 10px',
                                                                        borderRadius: 999,
                                                                        fontSize: 11.5,
                                                                        fontWeight: 800,
                                                                    }}
                                                                >
                                                                    {state}
                                                                    <span
                                                                        className="text-[var(--cpm-text-tertiary)]"
                                                                        style={{ fontWeight: 700 }}
                                                                    >
                                                                        priced only on{' '}
                                                                        {truncName(vari.name)}
                                                                    </span>
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ) : null}

                                            {overlap.map((state) => {
                                                const spChosen = volMap[state] || null;
                                                const sDone = !!spChosen;
                                                const pa = findSp(a.stateProducts, state);
                                                const pb = findSp(b.stateProducts, state);
                                                const nestedOpts = [
                                                    { variant: a, sp: pa },
                                                    { variant: b, sp: pb },
                                                ];
                                                return (
                                                    <div
                                                        key={state}
                                                        className="flex flex-col"
                                                        style={{
                                                            gap: 10,
                                                            padding: '13px 14px',
                                                            borderRadius: 12,
                                                            border: `1px solid ${sDone ? 'var(--cpm-border-default)' : 'var(--cpm-warning-border)'}`,
                                                            background: sDone
                                                                ? '#fff'
                                                                : 'var(--cpm-warning-bg)',
                                                        }}
                                                    >
                                                        <div
                                                            className="flex items-center flex-wrap"
                                                            style={{ gap: 10 }}
                                                        >
                                                            <span
                                                                className="text-[var(--cpm-brand-navy)]"
                                                                style={{
                                                                    fontSize: 13.5,
                                                                    fontWeight: 800,
                                                                }}
                                                            >
                                                                {state}
                                                            </span>
                                                            <span
                                                                className="inline-flex items-center rounded-full uppercase"
                                                                style={{
                                                                    padding: '3px 9px',
                                                                    fontSize: 9.5,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '0.06em',
                                                                    background: sDone
                                                                        ? 'var(--cpm-success-bg)'
                                                                        : 'var(--cpm-warning-bg)',
                                                                    border: `1px solid ${sDone ? 'var(--cpm-success-border)' : 'var(--cpm-warning-border)'}`,
                                                                    color: sDone
                                                                        ? 'var(--cpm-success-text-light)'
                                                                        : 'var(--cpm-warning-text)',
                                                                }}
                                                            >
                                                                {sDone
                                                                    ? 'Resolved'
                                                                    : 'Decision needed'}
                                                            </span>
                                                            <span
                                                                className="text-[var(--cpm-text-secondary)] text-pretty"
                                                                style={{
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {sDone
                                                                    ? 'Kept pricing chosen — the other variant’s row for this state is retired.'
                                                                    : 'Both variants price this state — pick the pricing row that survives.'}
                                                            </span>
                                                        </div>
                                                        <div
                                                            className="grid"
                                                            style={{
                                                                gridTemplateColumns:
                                                                    'repeat(auto-fit, minmax(250px, 1fr))',
                                                                gap: 10,
                                                            }}
                                                        >
                                                            {nestedOpts.map((opt) => {
                                                                const isChosen =
                                                                    spChosen === opt.variant._id;
                                                                const role = !chosenId
                                                                    ? 'none'
                                                                    : chosenId === opt.variant._id
                                                                      ? 'survivor'
                                                                      : 'loser';
                                                                const otherSp =
                                                                    opt.variant._id === a._id
                                                                        ? pb
                                                                        : pa;
                                                                const fields = spFieldRows(opt.sp);
                                                                const otherFields =
                                                                    spFieldRows(otherSp);
                                                                return (
                                                                    <button
                                                                        key={opt.variant._id}
                                                                        type="button"
                                                                        onClick={() =>
                                                                            onPickVolState(
                                                                                key,
                                                                                state,
                                                                                opt.variant._id
                                                                            )
                                                                        }
                                                                        className="flex flex-col text-left bg-white"
                                                                        style={{
                                                                            gap: 9,
                                                                            padding: '13px 14px',
                                                                            borderRadius: 12,
                                                                            border: `1.5px solid ${isChosen ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-default)'}`,
                                                                            background: isChosen
                                                                                ? 'rgba(110,100,240,.07)'
                                                                                : '#fff',
                                                                            boxShadow: isChosen
                                                                                ? '0 0 0 3px rgba(110,100,240,.20)'
                                                                                : undefined,
                                                                        }}
                                                                    >
                                                                        <div
                                                                            className="flex items-center"
                                                                            style={{ gap: 8 }}
                                                                        >
                                                                            <RadioDot
                                                                                chosen={isChosen}
                                                                            />
                                                                            <span
                                                                                className="min-w-0 truncate text-[var(--cpm-text-primary)]"
                                                                                style={{
                                                                                    fontSize: 12,
                                                                                    fontWeight: 800,
                                                                                }}
                                                                            >
                                                                                {opt.variant.name}
                                                                            </span>
                                                                            <span
                                                                                className="ml-auto inline-flex items-center rounded-full uppercase shrink-0"
                                                                                style={{
                                                                                    padding:
                                                                                        '2px 8px',
                                                                                    fontSize: 9.5,
                                                                                    fontWeight: 800,
                                                                                    letterSpacing:
                                                                                        '0.05em',
                                                                                    background:
                                                                                        role ===
                                                                                        'survivor'
                                                                                            ? 'var(--cpm-purple-tint-13)'
                                                                                            : role ===
                                                                                                'loser'
                                                                                              ? 'var(--cpm-shortfall-bg)'
                                                                                              : 'var(--cpm-surface-skeleton)',
                                                                                    border:
                                                                                        role ===
                                                                                        'loser'
                                                                                            ? '1px solid var(--cpm-shortfall-border)'
                                                                                            : undefined,
                                                                                    color:
                                                                                        role ===
                                                                                        'survivor'
                                                                                            ? 'var(--cpm-brand-purple)'
                                                                                            : role ===
                                                                                                'loser'
                                                                                              ? 'var(--cpm-shortfall-text)'
                                                                                              : 'var(--cpm-text-secondary)',
                                                                                }}
                                                                            >
                                                                                {role === 'survivor'
                                                                                    ? 'Surviving variant'
                                                                                    : role ===
                                                                                        'loser'
                                                                                      ? 'Losing variant'
                                                                                      : 'On file'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex flex-col">
                                                                            {fields.map((f, i) => {
                                                                                const differs =
                                                                                    f.value !==
                                                                                    otherFields[i]
                                                                                        ?.value;
                                                                                return (
                                                                                    <div
                                                                                        key={f.key}
                                                                                        className="flex items-center justify-between"
                                                                                        style={{
                                                                                            gap: 10,
                                                                                            padding:
                                                                                                '6px 0',
                                                                                            borderBottom:
                                                                                                i <
                                                                                                fields.length -
                                                                                                    1
                                                                                                    ? '1px solid var(--cpm-border-default)'
                                                                                                    : undefined,
                                                                                        }}
                                                                                    >
                                                                                        <span
                                                                                            className="uppercase text-[var(--cpm-text-tertiary)]"
                                                                                            style={{
                                                                                                fontSize: 10.5,
                                                                                                fontWeight: 800,
                                                                                                letterSpacing:
                                                                                                    '0.05em',
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                f.label
                                                                                            }
                                                                                        </span>
                                                                                        <span
                                                                                            className="tabular text-right"
                                                                                            style={{
                                                                                                fontSize: 12.5,
                                                                                                fontWeight: 800,
                                                                                                color: differs
                                                                                                    ? 'var(--cpm-brand-navy)'
                                                                                                    : 'var(--cpm-text-secondary)',
                                                                                                background:
                                                                                                    differs
                                                                                                        ? 'rgba(110,100,240,.09)'
                                                                                                        : undefined,
                                                                                                borderRadius:
                                                                                                    differs
                                                                                                        ? 6
                                                                                                        : undefined,
                                                                                                padding:
                                                                                                    differs
                                                                                                        ? '1px 6px'
                                                                                                        : undefined,
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                f.value
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/** True when every colliding volume (and nested overlapping state) has a pick. */
export function productCollisionsResolved(
    sideA: CpmMasterDetailsResponse | null,
    sideB: CpmMasterDetailsResponse | null,
    volumeChoices: Record<string, string>,
    volStateChoices: Record<string, Record<string, string>>
): boolean {
    if (!sideA || !sideB) return true;
    const variantsA = sideA.affectedVariants ?? [];
    const variantsB = sideB.affectedVariants ?? [];
    const collidingKeys = Array.from(
        new Set([...variantsA, ...variantsB].map((v) => volumeKeyOf(v)))
    ).filter(
        (key) =>
            variantsA.some((v) => volumeKeyOf(v) === key) &&
            variantsB.some((v) => volumeKeyOf(v) === key)
    );
    for (const key of collidingKeys) {
        const survivingId = volumeChoices[key];
        if (!survivingId) return false;
        const a = variantsA.find((v) => volumeKeyOf(v) === key);
        const b = variantsB.find((v) => volumeKeyOf(v) === key);
        if (!a || !b) continue;
        const states = Array.from(
            new Set(
                [...(a.stateProducts ?? []), ...(b.stateProducts ?? [])]
                    .map((sp) => String(sp.state ?? ''))
                    .filter(Boolean)
            )
        );
        const overlap = states.filter(
            (s) => findSp(a.stateProducts, s) && findSp(b.stateProducts, s)
        );
        const map = volStateChoices[key] ?? {};
        if (overlap.some((s) => !map[s])) return false;
    }
    return true;
}

export function buildProductVolumeChoicesPayload(
    sideA: CpmMasterDetailsResponse,
    sideB: CpmMasterDetailsResponse,
    volumeChoices: Record<string, string>,
    volStateChoices: Record<string, Record<string, string>>
): Array<{
    volumeKey: string;
    survivingVariantId: string;
    stateChoices?: Record<string, 'survivor' | 'loser'>;
}> {
    const variantsA = sideA.affectedVariants ?? [];
    const variantsB = sideB.affectedVariants ?? [];
    const collidingKeys = Array.from(
        new Set([...variantsA, ...variantsB].map((v) => volumeKeyOf(v)))
    ).filter(
        (key) =>
            variantsA.some((v) => volumeKeyOf(v) === key) &&
            variantsB.some((v) => volumeKeyOf(v) === key)
    );

    return collidingKeys.map((volumeKey) => {
        const survivingVariantId = volumeChoices[volumeKey];
        const a = variantsA.find((v) => volumeKeyOf(v) === volumeKey)!;
        const b = variantsB.find((v) => volumeKeyOf(v) === volumeKey)!;
        const states = Array.from(
            new Set(
                [...(a.stateProducts ?? []), ...(b.stateProducts ?? [])]
                    .map((sp) => String(sp.state ?? ''))
                    .filter(Boolean)
            )
        );
        const overlap = states.filter(
            (s) => findSp(a.stateProducts, s) && findSp(b.stateProducts, s)
        );
        const map = volStateChoices[volumeKey] ?? {};
        const stateChoices: Record<string, 'survivor' | 'loser'> = {};
        for (const state of overlap) {
            const picked = map[state];
            if (!picked) continue;
            stateChoices[state] = picked === survivingVariantId ? 'survivor' : 'loser';
        }
        return {
            volumeKey,
            survivingVariantId,
            ...(overlap.length > 0 ? { stateChoices } : {}),
        };
    });
}
